import { useTranslation } from 'next-i18next'
import { useEffect } from 'react'
import { NEAR_USER } from 'src/constants'
import ga from 'src/utils/pixels/gtag'
import {
  IGoogleAutocompleteSuggestion,
  IGooglePlaceDetails
} from '../../types/graphql'
import useAutocomplete from '../AutoComplete/useAutocomplete'
import { SVG_ICONS } from './constants'

interface LocationAutocompleteProps {
  onLocationSelect: (
    placeId: string,
    description: string,
    coords?: IGooglePlaceDetails
  ) => void
  searchValue: string
}

const LocationAutocomplete = ({
  onLocationSelect,
  searchValue
}: LocationAutocompleteProps) => {
  const { t } = useTranslation('common')

  const { options, loading, handleOnChange } = useAutocomplete({
    handleOnSelect: () => {}
  })

  useEffect(() => {
    if (searchValue !== undefined) {
      handleOnChange({
        target: { value: searchValue }
      } as React.ChangeEvent<HTMLInputElement>)
    }
  }, [searchValue, handleOnChange])

  const handleOptionClick = (option: IGoogleAutocompleteSuggestion) => {
    // Middle step of the nav-search funnel (search_box_start → HERE →
    // search → search_completed). With an empty input the dropdown shows the
    // CDMX/MTY presets (+ near-me), so `searchValue` splits typed Google
    // Places picks from preset taps. LocationAutocomplete is only consumed by
    // the nav SearchBar/MobileSearchBar — do NOT reuse this event from the
    // shared useAutocomplete hook (it also serves add-listing / checkout
    // address fields, which are not search).
    ga.event('search_suggestion_select', {
      suggestion_type:
        option.placeId === NEAR_USER
          ? 'near_me'
          : searchValue
            ? 'place'
            : 'city_preset',
      description: option.description
    })
    onLocationSelect(option.placeId, option.friendlyDisplay)
  }

  return (
    <div className="overflow-hidden p-3 md:p-4">
      <div className="max-h-64 overflow-y-auto">
        {loading && (
          <div className="flex items-center justify-center py-6 md:py-8">
            <div className="size-5 animate-spin rounded-full border-2 border-ink-100/20 border-t-blue-500 md:size-6"></div>
          </div>
        )}

        {!loading && options.length === 0 && searchValue && (
          <div className="py-3 text-center text-ink-200 md:py-4">
            <p className="text-xs md:text-sm">
              {t('SEARCH_BAR.NO_LOCATIONS_FOUND')}
            </p>
          </div>
        )}

        {!loading && options.length > 0 && (
          <div className="space-y-0.5 md:space-y-1">
            {options.map((option, index) => (
              <button
                key={option.placeId || index}
                type="button"
                onClick={() => handleOptionClick(option)}
                className="group flex w-full items-center space-x-2.5 overflow-hidden rounded-lg px-2.5 py-2.5 text-left transition-colors hover:bg-cream-300/60 focus-visible:bg-cream-300/60 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-blue-800 md:space-x-3 md:px-3"
              >
                <div className="shrink-0 text-ink-200 transition-colors group-hover:text-blue-800">
                  {SVG_ICONS.location}
                </div>
                <span className="min-w-0 flex-1 truncate text-sm text-ink-700 md:text-sm">
                  {option.description}
                </span>
              </button>
            ))}
          </div>
        )}
      </div>
    </div>
  )
}

export default LocationAutocomplete
